import { useState, useRef, useEffect } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase/config";

/**
 * PhotoUpload — file picker that uploads to Firebase Storage and calls
 * onUpload(url) when done, or onUpload(null) on clear/error.
 *
 * Props:
 *   childId   {string}   — used for the storage path: journal/{childId}/{ts}_{name}
 *   onUpload  {function} — called with the download URL on success, null on clear
 *   disabled  {boolean}
 *   value     {string|null} — controlled: when parent resets to null the preview clears
 */
const PhotoUpload = ({ childId, onUpload, disabled, value }) => {
  const [uploading, setUploading] = useState(false);
  const [preview,   setPreview]   = useState(null);
  const inputRef = useRef(null);

  // Sync preview when parent resets the value to null (e.g. after form submit)
  useEffect(() => {
    if (!value) {
      setPreview(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [value]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be under 5 MB.");
      return;
    }

    // Show local preview immediately while uploading
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const storageRef = ref(storage, `journal/${childId}/${Date.now()}_${file.name}`);
      const snapshot   = await uploadBytes(storageRef, file);
      const url        = await getDownloadURL(snapshot.ref);
      onUpload(url);
    } catch (err) {
      console.error("Photo upload failed:", err);
      const msg = err?.code === 'storage/unauthorized'
        ? "Upload failed: storage permissions not configured. Please contact support."
        : "Photo upload failed — please check your connection and try again.";
      alert(msg);
      setPreview(null);
      onUpload(null);
      if (inputRef.current) inputRef.current.value = '';
    } finally {
      setUploading(false);
    }
  };

  const clear = () => {
    setPreview(null);
    onUpload(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  if (preview) {
    return (
      <div style={{ position: "relative", display: "inline-block" }}>
        <img
          src={preview}
          alt="Preview"
          style={{ maxHeight: "100px", maxWidth: "200px", borderRadius: "8px", objectFit: "cover" }}
        />
        {uploading && (
          <div style={{
            position: "absolute", inset: 0, background: "rgba(255,255,255,0.7)",
            borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "0.75rem", color: "var(--text-dark)",
          }}>
            Uploading…
          </div>
        )}
        {!uploading && (
          <button
            type="button"
            onClick={clear}
            style={{
              position: "absolute", top: "3px", right: "3px",
              background: "rgba(0,0,0,0.55)", color: "white", border: "none",
              borderRadius: "50%", width: "20px", height: "20px",
              cursor: "pointer", fontSize: "0.65rem",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >✕</button>
        )}
      </div>
    );
  }

  return (
    <label style={{
      display: "inline-flex", alignItems: "center", gap: "0.4rem",
      padding: "0.3rem 0.7rem",
      border: "1.5px dashed var(--border-light)", borderRadius: "8px",
      cursor: disabled ? "not-allowed" : "pointer",
      fontSize: "0.82rem", color: "var(--text-light)",
      opacity: disabled ? 0.5 : 1,
      transition: "border-color 0.2s",
    }}>
      📷 Add photo
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        disabled={disabled}
        style={{ display: "none" }}
      />
    </label>
  );
};

export default PhotoUpload;
